import { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { api, apiErrorMessage, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Plus, Users2, Gavel, FileCheck, Vote, ShieldCheck, Lock, Sparkles, ThumbsUp, ThumbsDown, Mail, EyeOff, UserCheck, UserX, UserMinus, ScrollText, ClipboardCheck, Printer, Send, BookOpen, Eye } from "lucide-react";
import { useProperty } from "@/contexts/PropertyContext";
import { DateTimePicker } from "@/components/DateTimePicker";
import { PresenceStrip } from "@/components/PresenceStrip";

const STATUS_COLORS = {
  scheduled: "border-sky-500/40 text-sky-300 bg-sky-500/10",
  in_progress: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  completed: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  archived: "border-zinc-500/40 text-zinc-300 bg-zinc-500/10",
};

function CreateMeetingDialog({ propertyId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // Track which AI-drafted titles are on-screen so we can rate them.
  // {[title]: "up"|"down"|undefined}
  const [aiRatings, setAiRatings] = useState({});
  const [form, setForm] = useState({
    title: "", type: "regular",
    scheduled_at: new Date(Date.now() + 7 * 24 * 3600 * 1000),
    quorum_required: 3, agenda_raw: "Approve prior minutes\nTreasurer report\nOld business\nNew business\nAdjournment",
    location: "", zoom_link: "",
    ai_drafted_titles: [],   // populated by draftWithAI so we know which ones to rate
  });
  const draftWithAI = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    setDrafting(true);
    try {
      const { data } = await api.post("/board/meetings/agenda-draft", { property_id: propertyId });
      const agendaLines = (data.agenda || []).map((a) => a.title).filter(Boolean);
      if (agendaLines.length) {
        setForm((f) => ({ ...f, agenda_raw: agendaLines.join("\n"), ai_drafted_titles: agendaLines }));
        setAiRatings({});
        toast.success(`AI drafted ${agendaLines.length} agenda items`, {
          description: data.notes ? String(data.notes).slice(0, 120) : undefined,
        });
      } else toast.info("AI returned no items — try again with more backlog");
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setDrafting(false);
  };
  const rateItem = async (title, rating) => {
    setAiRatings((r) => ({ ...r, [title]: rating }));
    try {
      await api.post("/board/meetings/agenda-draft/rate", {
        property_id: propertyId, item_title: title, rating,
      });
      toast.success(rating === "up" ? "Thanks — noted as a keeper" : "Noted — the AI will avoid this next time");
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/BoardMeetingsPage.jsx: swallowed', err); }
  };
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (!propertyId) return toast.error("Pick a property first");
    if (!form.scheduled_at) return toast.error("Pick a date and time");
    setSaving(true);
    try {
      const agenda = form.agenda_raw.split("\n").map((l) => l.trim()).filter(Boolean).map((title) => ({ title, description: "", duration_min: 10 }));
      await api.post("/board/meetings", {
        property_id: propertyId, title: form.title, type: form.type,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        quorum_required: parseInt(form.quorum_required, 10) || 3, agenda,
        location: form.location.trim() || null,
        zoom_link: form.zoom_link.trim() || null,
      });
      toast.success("Meeting scheduled");
      setOpen(false); onCreated();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-meeting-btn" className="bg-orange-500 hover:bg-orange-600 rounded-full"><Plus className="h-4 w-4 mr-1" /> New meeting</Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg">
        <DialogHeader><DialogTitle>Schedule board meeting</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="meeting-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="February 2026 regular meeting" className="mt-1 bg-white/[0.04] border-white/10" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="meeting-type" className="mt-1 bg-white/[0.04] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>{["regular", "special", "annual", "emergency"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quorum required</Label><Input data-testid="meeting-quorum" type="number" min="0" value={form.quorum_required} onChange={(e) => setForm({ ...form, quorum_required: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
          </div>
          <div><Label>Scheduled at</Label><div className="mt-1"><DateTimePicker value={form.scheduled_at} onChange={(d) => setForm({ ...form, scheduled_at: d })} testIdPrefix="meeting-datetime" /></div></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Location <span className="text-zinc-500 text-[10px]">(optional)</span></Label>
              <Input data-testid="meeting-location" value={form.location}
                     onChange={(e) => setForm({ ...form, location: e.target.value })}
                     placeholder="Community clubhouse, room 2A" className="mt-1 bg-white/[0.04] border-white/10" />
            </div>
            <div>
              <Label>Zoom / virtual link <span className="text-zinc-500 text-[10px]">(optional)</span></Label>
              <Input data-testid="meeting-zoom-link" type="url" value={form.zoom_link}
                     onChange={(e) => setForm({ ...form, zoom_link: e.target.value })}
                     placeholder="https://zoom.us/j/…" className="mt-1 bg-white/[0.04] border-white/10" />
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 -mt-1">Both fields ship with the automated 24h reminder email.</div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Agenda (one per line)</Label>
              <Button size="sm" variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10 hover:bg-orange-500/20 h-7 text-[11px]"
                onClick={draftWithAI} disabled={drafting} data-testid="ai-draft-btn">
                <Sparkles className="h-3 w-3 mr-1" /> {drafting ? "Drafting…" : "Draft with AI"}
              </Button>
            </div>
            <Textarea data-testid="meeting-agenda" rows={6} value={form.agenda_raw} onChange={(e) => setForm({ ...form, agenda_raw: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10 font-mono text-xs" />
            {form.ai_drafted_titles?.length > 0 && (
              <div className="mt-2 rounded border border-orange-500/20 bg-orange-500/[0.03] p-2" data-testid="ai-rate-block">
                <div className="text-[10px] uppercase tracking-widest text-orange-300/70 mb-2 flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Was this helpful? Rate the items so the AI gets sharper.
                </div>
                <div className="space-y-1">
                  {form.ai_drafted_titles.map((title) => {
                    const r = aiRatings[title];
                    return (
                      <div key={title} className="flex items-center gap-2 text-[11px]">
                        <span className="flex-1 truncate text-zinc-300">{title}</span>
                        <button onClick={() => rateItem(title, "up")}
                          data-testid={`rate-up-${title.slice(0, 12)}`}
                          className={`p-1 rounded ${r === "up" ? "text-emerald-400 bg-emerald-500/10" : "text-zinc-500 hover:text-emerald-400"}`}>
                          <ThumbsUp className="h-3 w-3" />
                        </button>
                        <button onClick={() => rateItem(title, "down")}
                          data-testid={`rate-down-${title.slice(0, 12)}`}
                          className={`p-1 rounded ${r === "down" ? "text-rose-400 bg-rose-500/10" : "text-zinc-500 hover:text-rose-400"}`}>
                          <ThumbsDown className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <Button data-testid="save-meeting" disabled={saving} onClick={save} className="w-full bg-orange-500 hover:bg-orange-600">{saving ? "Scheduling…" : "Schedule"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttendancePanel({ meetingId, meeting, locked, onChange }) {
  // R108: unified attendance panel — one row per active board seat with
  // present/absent/excused/proxy radios + optional proxy holder selector +
  // per-row notes. "Check me in" button is exposed only to seated board
  // members and self-marks the caller as present.
  const [roster, setRoster] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: u }] = await Promise.all([
        api.get(`/board/meetings/${meetingId}/roster`),
        api.get("/auth/me"),
      ]);
      setRoster(r); setMe(u);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load roster"); }
  }, [meetingId]);
  useEffect(() => { load(); }, [load]);

  const setRow = (uid, patch) => {
    setRoster((r) => ({
      ...r, roster: r.roster.map((row) => row.user_id === uid ? { ...row, ...patch, recorded: true } : row),
    }));
  };

  const saveAll = async () => {
    if (!roster) return;
    setSaving(true);
    try {
      const entries = roster.roster
        .filter((r) => !r.vacant)
        .map((r) => ({
          user_id: r.user_id, name: r.name, role: "board",
          status: r.status || "absent",
          proxy_holder_id: r.status === "proxy" ? (r.proxy_holder_id || null) : null,
          notes: r.notes || null,
        }));
      const { data } = await api.put(`/board/meetings/${meetingId}/attendance/bulk`, {
        entries, replace: true,
      });
      const cq = data.quorum || {};
      toast.success(`Attendance saved · ${cq.counting || 0}/${cq.required || 0}${cq.met ? " (quorum met)" : ""}`);
      onChange && onChange();
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to save"); }
    setSaving(false);
  };

  const selfCheckIn = async () => {
    setCheckingIn(true);
    try {
      const { data } = await api.post(`/board/meetings/${meetingId}/check-in`);
      const cq = data.quorum || {};
      toast.success(`You're checked in · ${cq.counting || 0}/${cq.required || 0}`);
      onChange && onChange();
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Cannot self check-in"); }
    setCheckingIn(false);
  };

  // R157B — Self-assign proxy: absent director designates a colleague to
  // hold their seat for THIS meeting.
  const [proxyDialogOpen, setProxyDialogOpen] = useState(false);
  const [proxyPick, setProxyPick] = useState("");
  const [proxyBusy, setProxyBusy] = useState(false);

  const assignMyProxy = async () => {
    if (!proxyPick) return toast.error("Pick a fellow director");
    setProxyBusy(true);
    try {
      const { data } = await api.post(`/board/meetings/${meetingId}/assign-proxy`,
        { proxy_holder_id: proxyPick });
      toast.success(`Proxy assigned to ${data.proxy_holder_name}`);
      setProxyDialogOpen(false);
      setProxyPick("");
      onChange && onChange();
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Cannot assign proxy"); }
    setProxyBusy(false);
  };

  if (!roster) return <div className="text-xs text-muted-foreground">Loading roster…</div>;
  const q = roster.quorum || {};
  const proxyOptions = roster.roster.filter((r) => !r.vacant);
  const canSelfCheckIn = me && !locked && (roster.roster.some((r) => r.user_id === me.id) || me.role === "board");

  return (
    <div data-testid="attendance-panel">
      <div className="flex items-center flex-wrap gap-2 mb-3">
        <div className="overline text-xs flex items-center gap-2"><Users2 className="h-3 w-3" /> Attendance & quorum</div>
        <Badge variant="outline" className={q.met ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-rose-500/40 text-rose-300 bg-rose-500/10"} data-testid="attendance-quorum-badge">
          {q.counting || 0}/{q.required || 0} {q.met ? "· quorum met" : "· quorum NOT met"}
        </Badge>
        <div className="flex gap-1">
          <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 text-[10px]">present {q.counts?.present || 0}</Badge>
          <Badge variant="outline" className="border-indigo-500/30 text-indigo-300 text-[10px]">proxy {q.counts?.proxy || 0}</Badge>
          <Badge variant="outline" className="border-amber-500/30 text-amber-300 text-[10px]">excused {q.counts?.excused || 0}</Badge>
          <Badge variant="outline" className="border-zinc-500/30 text-zinc-400 text-[10px]">absent {q.counts?.absent || 0}</Badge>
        </div>
        <div className="ml-auto flex gap-2">
          {canSelfCheckIn && (
            <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-300 h-7" onClick={selfCheckIn} disabled={checkingIn} data-testid="self-check-in-btn">
              <UserCheck className="h-3 w-3 mr-1" /> {checkingIn ? "Checking in…" : "Check me in"}
            </Button>
          )}
          {canSelfCheckIn && (
            <Button size="sm" variant="outline" className="border-indigo-500/30 text-indigo-300 h-7" onClick={() => setProxyDialogOpen(true)} data-testid="self-assign-proxy-btn">
              <UserMinus className="h-3 w-3 mr-1" /> Assign my proxy
            </Button>
          )}
          {!locked && (
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-7" onClick={saveAll} disabled={saving} data-testid="attendance-save">
              <ClipboardCheck className="h-3 w-3 mr-1" /> {saving ? "Saving…" : "Save roster"}
            </Button>
          )}
        </div>
      </div>

      {roster.roster.length === 0 ? (
        <div className="text-xs text-muted-foreground p-3 border border-dashed border-white/10 rounded">
          No active board seats configured —{" "}
          <a href="/admin/board-terms" className="text-orange-300 hover:text-orange-200 underline underline-offset-2" data-testid="attendance-empty-link">
            add seats under Governance → Board Terms
          </a>.
        </div>
      ) : (
        <div className="rounded border border-white/10 overflow-hidden">
          {roster.roster.map((row) => (
            <div key={row.user_id} className="border-b border-white/5 last:border-b-0 p-2 flex flex-wrap items-center gap-2" data-testid={`roster-row-${row.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{row.name}</div>
                <div className="text-[10px] text-zinc-500 flex items-center gap-2">
                  <span>{row.seat_title || "guest"}</span>
                  {row.term_class && <span>· Class {row.term_class}</span>}
                  {row.checked_in_at && <span className="text-emerald-400">· self check-in {new Date(row.checked_in_at).toLocaleTimeString()}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1" role="radiogroup" aria-label={`status-${row.user_id}`}>
                {[
                  { key: "present", label: "Present", tone: "emerald", icon: UserCheck },
                  { key: "proxy", label: "Proxy", tone: "indigo", icon: UserMinus },
                  { key: "excused", label: "Excused", tone: "amber", icon: ScrollText },
                  { key: "absent", label: "Absent", tone: "zinc", icon: UserX },
                ].map(({ key, label, tone, icon: Icon }) => {
                  const active = row.status === key;
                  return (
                    <button key={key}
                      disabled={locked}
                      onClick={() => setRow(row.user_id, { status: key })}
                      role="radio"
                      aria-checked={active}
                      data-testid={`att-${row.user_id}-${key}`}
                      className={`h-7 px-2 rounded-full text-[11px] border flex items-center gap-1 transition ${
                        active
                          ? `bg-${tone}-500/15 border-${tone}-500/40 text-${tone}-300`
                          : "border-white/10 text-zinc-400 hover:bg-white/[0.03]"
                      }`}>
                      <Icon className="h-3 w-3" /> {label}
                    </button>
                  );
                })}
              </div>
              {row.status === "proxy" && (
                <Select value={row.proxy_holder_id || ""} onValueChange={(v) => setRow(row.user_id, { proxy_holder_id: v })} disabled={locked}>
                  <SelectTrigger className="h-7 w-40 text-[11px] bg-white/[0.04] border-white/10" data-testid={`att-${row.user_id}-proxy-select`}>
                    <SelectValue placeholder="Held by…" />
                  </SelectTrigger>
                  <SelectContent>
                    {proxyOptions.filter((p) => p.user_id !== row.user_id).map((p) => (
                      <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>
      )}
      {/* R157B — self-assign proxy dialog */}
      <Dialog open={proxyDialogOpen} onOpenChange={setProxyDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[420px]"
                       data-testid="self-assign-proxy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="h-4 w-4 text-indigo-300" /> Assign my proxy
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-white/60">
              Can't make this meeting? Pick a fellow director to hold your seat.
              A proxy still counts toward quorum, and you'll show up in the roster
              as <b className="text-indigo-300">proxy</b> until you're actually present.
            </div>
            <Select value={proxyPick} onValueChange={setProxyPick}>
              <SelectTrigger className="bg-white/[0.04] border-white/10"
                              data-testid="self-assign-proxy-select">
                <SelectValue placeholder="Choose a fellow director…" />
              </SelectTrigger>
              <SelectContent>
                {proxyOptions.filter((p) => me && p.user_id !== me.id).map((p) => (
                  <SelectItem key={p.user_id} value={p.user_id}>
                    {p.name} · {p.seat_title || "seat"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProxyDialogOpen(false)}
                     className="border-white/10">Cancel</Button>
            <Button onClick={assignMyProxy} disabled={proxyBusy || !proxyPick}
                     className="bg-indigo-500 hover:bg-indigo-600"
                     data-testid="self-assign-proxy-save">
              {proxyBusy ? "Assigning…" : "Assign proxy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MeetingRow({ m, onOpen }) {
  const dt = new Date(m.scheduled_at).toLocaleString();
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between hover:bg-white/[0.04] transition cursor-pointer" data-testid={`meeting-${m.id}`} onClick={() => onOpen(m.id)}>
      <div className="min-w-0">
        <div className="font-medium truncate flex items-center gap-2"><CalendarClock className="h-4 w-4 text-orange-400" /> {m.title}</div>
        <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
          <span>{dt}</span><span>·</span><span className="capitalize">{m.type}</span><span>·</span><span>Quorum {m.quorum_required}</span>
          <span>·</span><span>{(m.motions || []).length} motions</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {m.minutes_hash && <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10"><Lock className="h-3 w-3 mr-1" /> Finalized</Badge>}
        <Badge variant="outline" className={STATUS_COLORS[m.status] || "border-white/10"}>{m.status}</Badge>
      </div>
    </div>
  );
}

function DigestLogBlock({ meetingId }) {
  // Once a meeting is finalized, show a per-recipient delivery log with
  // status counts + top rows. Auto-refreshes every 30s so opens/bounces
  // (fed by the digest webhook) appear without a page reload.
  const [log, setLog] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/board/meetings/${meetingId}/digest-log`);
      setLog(data);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/BoardMeetingsPage.jsx: swallowed', err); }
  }, [meetingId]);
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);
  if (!log || !log.total) return null;
  const c = log.counts || {};
  return (
    <div className="mt-4" data-testid="digest-log">
      <div className="overline text-xs mb-2 flex items-center gap-2">
        <Mail className="h-3 w-3" /> Digest send log ({log.total})
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px]">sent {c.sent || 0}</Badge>
        {c.queued > 0 && <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10 text-[10px]">queued {c.queued}</Badge>}
        {c.opened > 0 && <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 bg-indigo-500/10 text-[10px]">opened {c.opened}</Badge>}
        {c.bounced > 0 && <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10 text-[10px]">bounced {c.bounced}</Badge>}
        <button onClick={() => setExpanded(!expanded)} className="ml-auto text-[10px] text-zinc-400 hover:text-zinc-200" data-testid="digest-log-toggle">
          {expanded ? "Hide rows" : "Show rows"}
        </button>
      </div>
      {expanded && (
        <div className="max-h-64 overflow-y-auto rounded border border-white/10 bg-white/[0.02]">
          <table className="w-full text-[11px]">
            <thead className="text-zinc-500 sticky top-0 bg-card">
              <tr>
                <th className="text-left px-2 py-1.5">Recipient</th>
                <th className="text-left px-2 py-1.5">Email</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-left px-2 py-1.5">Sent</th>
                <th className="text-left px-2 py-1.5">Opened</th>
              </tr>
            </thead>
            <tbody>
              {log.items.slice(0, 200).map((r) => (
                <tr key={r.id} className="border-t border-white/[0.03]" data-testid={`digest-row-${r.id}`}>
                  <td className="px-2 py-1 truncate max-w-[160px]">{r.resident_name || "—"}</td>
                  <td className="px-2 py-1 truncate max-w-[220px] text-zinc-500">{r.email}</td>
                  <td className="px-2 py-1">
                    <span className={
                      r.bounced_at ? "text-rose-300"
                      : r.opened_at ? "text-indigo-300"
                      : r.status === "sent" ? "text-emerald-300"
                      : "text-amber-300"
                    }>
                      {r.bounced_at ? "bounced" : r.opened_at ? "opened" : r.status}
                    </span>
                    {r.bounce_reason && <span className="text-[10px] text-rose-400 ml-1">({r.bounce_reason})</span>}
                  </td>
                  <td className="px-2 py-1 text-zinc-500">{r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}</td>
                  <td className="px-2 py-1 text-zinc-500">{r.opened_at ? new Date(r.opened_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MeetingDetail({ meetingId, onBack, onChange }) {
  const [m, setM] = useState(null);
  const [minutes, setMinutes] = useState("");
  const [motionText, setMotionText] = useState("");
  const [linkedSaId, setLinkedSaId] = useState("");
  const [draftSAs, setDraftSAs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [skipDigest, setSkipDigest] = useState(false);
  const [attendee, setAttendee] = useState({ user_id: "", name: "", attended: true });
  const [blasting, setBlasting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templateFilter, setTemplateFilter] = useState("");
  // R161 — remember which template the current motionText came from so
  // the propose call can back-link it (enables prefill-last-adopted on
  // the next preview run).
  const [motionTemplate, setMotionTemplate] = useState(null);

  const blastAgenda = async () => {
    if (!window.confirm("Email this agenda to every homeowner in this community?\n\nAttaches the PDF in the community's language. Deduped by email address.")) return;
    setBlasting(true);
    try {
      const { data } = await api.post(`/board/meetings/${meetingId}/blast-agenda`, {});
      toast.success(`Agenda blasted — ${data.sent} sent · ${data.queued} queued · ${data.recipients} recipients`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Blast failed"); }
    setBlasting(false);
  };

  const loadTemplates = useCallback(async () => {
    if (!m?.property_id) return;
    try {
      const { data } = await api.get(`/board/motion-templates?property_id=${m.property_id}`);
      setTemplates(data.items || []);
    } catch { setTemplates([]); }
  }, [m?.property_id]);
  useEffect(() => { if (m?.property_id) loadTemplates(); }, [m?.property_id, loadTemplates]);

  const applyTemplate = (tpl) => {
    setMotionText(tpl.body);
    setMotionTemplate({ id: tpl.id, code: tpl.code });   // R161
    setTemplatesOpen(false);
    toast.info(`Loaded: ${tpl.title} — fill in the {placeholders}`);
  };

  const previewTemplate = async (tpl) => {
    // R160/R161 — open the motion preview PDF in a new tab.  Order of
    // precedence for the body:
    //   1. Whatever's in the composer right now (user edits win)
    //   2. The last-adopted version of this template from a past meeting
    //   3. The raw template body (with placeholders visible)
    try {
      const bodyOverride = motionText && motionText.trim().length > 0 ? motionText : null;
      const res = await fetch(
        `${API}/api/board/motion-templates/${tpl.id}/preview.pdf`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            body: bodyOverride,
            meeting_title: m?.title || null,
            prefill_last_adopted: !bodyOverride,   // R161
            property_id: m?.property_id || null,
          }),
        }
      );
      if (!res.ok) throw new Error(`Preview failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (!w) toast.error("Popup blocked — allow popups to see the preview");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/board/meetings/${meetingId}`);
      setM(data); setMinutes(data.minutes_draft || "");
      // Load draft special assessments for this property so admins can link a motion to one.
      if (data.property_id) {
        try {
          const { data: sa } = await api.get(`/admin/special-assessments`, {
            params: { property_id: data.property_id, status: "draft" },
          });
          setDraftSAs(sa.items || []);
        } catch { setDraftSAs([]); }
      }
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, [meetingId]);
  useEffect(() => { load(); }, [load]);
  if (!m) return <Card className="border-white/10 bg-card p-5 text-sm text-muted-foreground">Loading…</Card>;

  const locked = !!m.minutes_hash;

  const openPreview = async () => {
    try {
      const { data } = await api.get(`/board/meetings/${meetingId}/digest-preview`);
      setPreview(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const addAttendee = async () => {
    if (!attendee.user_id.trim() || !attendee.name.trim()) return toast.error("Enter director ID and name");
    try { await api.post(`/board/meetings/${meetingId}/attendance`, attendee); setAttendee({ user_id: "", name: "", attended: true }); load(); onChange(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const propose = async () => {
    if (!motionText.trim()) return toast.error("Motion text required");
    const attendedIds = (m.attendees || []).filter((a) => a.attended);
    const proposer = attendedIds[0]?.user_id || "unknown";
    try {
      await api.post(`/board/meetings/${meetingId}/motions`, {
        text: motionText, proposed_by: proposer,
        special_assessment_id: linkedSaId || undefined,
        // R161 — carry the template ID/code so the next preview run can
        // prefill from this adopted motion.
        template_id: motionTemplate?.id || undefined,
        template_code: motionTemplate?.code || undefined,
      });
      setMotionText("");
      setMotionTemplate(null);
      setLinkedSaId("");
      if (linkedSaId) toast.success("Motion linked to special assessment — cron will bill on approval");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const vote = async (motion_id, v) => {
    const attended = (m.attendees || []).filter((a) => a.attended);
    if (!attended.length) return toast.error("Mark attendance first");
    // Cast a vote as every attended director (simulated live meeting)
    const dir = attended.find((a) => !((m.motions.find((mo) => mo.id === motion_id)?.votes || {})[a.user_id]));
    if (!dir) return toast.info("All attended directors have voted");
    try { await api.post(`/board/meetings/${meetingId}/motions/${motion_id}/vote`, { user_id: dir.user_id, voter_name: dir.name, vote: v }); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const saveMinutes = async () => {
    try { await api.put(`/board/meetings/${meetingId}/minutes`, { minutes_draft: minutes }); toast.success("Draft saved"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const finalize = async () => {
    if (!minutes.trim()) return toast.error("Minutes required to finalize");
    const skipCopy = skipDigest ? " · resident digest will be SKIPPED (confidential meeting)" : " · resident digest will be sent";
    if (!window.confirm(`Finalize this meeting? It becomes hash-locked and read-only.${skipCopy}`)) return;
    try {
      const { data } = await api.post(`/board/meetings/${meetingId}/finalize`, { minutes_final: minutes, skip_digest: skipDigest });
      toast.success(`Locked with hash ${data.hash.slice(0, 8)}…${data.digest_sent ? " · digest queued" : " · digest skipped"}`);
      load(); onChange();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const tally = (motion) => {
    const votes = Object.values(motion.votes || {});
    return { yes: votes.filter((v) => v.vote === "yes").length, no: votes.filter((v) => v.vote === "no").length, abstain: votes.filter((v) => v.vote === "abstain").length };
  };

  return (
    <Card className="border-white/10 bg-card p-5 space-y-4" data-testid="meeting-detail">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">{m.type} meeting</div>
          <div className="text-lg font-semibold">{m.title}</div>
          <div className="text-xs text-zinc-500">{new Date(m.scheduled_at).toLocaleString()} · Quorum {m.attendees.filter((a) => (a.status === "present" || a.status === "proxy") || (!a.status && a.attended)).length}/{m.quorum_required} {m.quorum_met && <span className="text-emerald-400">✓ met</span>}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-white/10" onClick={onBack}>Back</Button>
          {!locked && <>
            <Button data-testid="preview-digest-btn" size="sm" variant="outline" className="border-white/10" onClick={openPreview}><Mail className="h-3 w-3 mr-1" /> Preview digest</Button>
            <a href={`${API}/board/meetings/${meetingId}/agenda.pdf`}
               target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 rounded-md border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 text-xs px-3 h-8"
               data-testid="print-agenda-btn"
               title="Printable meeting agenda with ballot QR embedded">
              <Printer className="h-3 w-3" /> Print agenda
            </a>
            <Button data-testid="blast-agenda-btn" size="sm" variant="outline"
                    className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 h-8"
                    onClick={blastAgenda} disabled={blasting}
                    title="Email this agenda to every homeowner (48h reminder before the meeting)">
              <Send className="h-3 w-3 mr-1" /> {blasting ? "Blasting…" : "Blast agenda"}
            </Button>
            <label className="text-[11px] text-zinc-400 inline-flex items-center gap-1 select-none">
              <input type="checkbox" data-testid="skip-digest" checked={skipDigest} onChange={(e) => setSkipDigest(e.target.checked)} className="rounded" />
              <EyeOff className="h-3 w-3" /> Skip digest
            </label>
            <Button data-testid="finalize-meeting" onClick={finalize} className="bg-emerald-600 hover:bg-emerald-700"><Lock className="h-4 w-4 mr-1" /> Finalize</Button>
          </>}
        </div>
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="digest-preview-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b flex items-center justify-between">
              <div className="text-xs text-zinc-500">Resident digest preview — {preview.recipients} recipients</div>
              <Button size="sm" variant="outline" onClick={() => setPreview(null)}>Close</Button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html || "") }} />
          </div>
        </div>
      )}
      {locked && (
        <DigestLogBlock meetingId={meetingId} />
      )}
      {locked && <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200"><ShieldCheck className="h-4 w-4 inline mr-1" /> Hash: <code className="font-mono">{m.minutes_hash.slice(0, 32)}…</code></div>}

      <div>
        <AttendancePanel meetingId={meetingId} meeting={m} locked={locked} onChange={() => { load(); onChange(); }} />
      </div>

      <div>
        <div className="overline text-xs mb-2 flex items-center gap-2"><Gavel className="h-3 w-3" /> Motions ({m.motions.length})</div>
        {!locked && (
          <>
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1 flex gap-2">
                <Input data-testid="motion-text" value={motionText} onChange={(e) => setMotionText(e.target.value)} placeholder="Motion to approve $X for Y…" className="bg-white/[0.04] border-white/10 text-xs" />
                <button
                  type="button"
                  onClick={() => setTemplatesOpen((v) => !v)}
                  className="inline-flex items-center gap-1 rounded-md border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 text-xs px-3 h-9"
                  data-testid="motion-templates-btn"
                  title="Insert a common motion from the library"
                >
                  <BookOpen className="h-3 w-3" /> Templates
                </button>
                {templatesOpen && (
                  <div className="absolute top-10 right-0 z-30 w-[420px] max-h-[420px] overflow-y-auto rounded-md border border-white/10 bg-[#0a0a0b] shadow-xl p-2"
                        data-testid="motion-templates-panel">
                    <div className="p-2 border-b border-white/10 mb-1">
                      <Input value={templateFilter} onChange={(e) => setTemplateFilter(e.target.value)}
                              placeholder="Filter templates…"
                              className="bg-white/[0.04] border-white/10 text-xs h-8"
                              data-testid="motion-template-filter" />
                    </div>
                    {templates.filter((t) =>
                      !templateFilter ||
                      (t.title + " " + t.category + " " + t.code).toLowerCase().includes(templateFilter.toLowerCase())
                    ).length === 0 ? (
                      <div className="text-xs text-white/40 p-3 text-center">No templates match.</div>
                    ) : (
                      templates.filter((t) =>
                        !templateFilter ||
                        (t.title + " " + t.category + " " + t.code).toLowerCase().includes(templateFilter.toLowerCase())
                      ).map((tpl) => (
                        <div key={tpl.id} className="flex items-stretch gap-1">
                          <button
                            type="button"
                            onClick={() => applyTemplate(tpl)}
                            className="flex-1 text-left rounded-md p-2 hover:bg-white/[0.04] transition text-xs"
                            data-testid={`motion-template-${tpl.code}`}
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              <div className="font-medium text-sm">{tpl.title}</div>
                              <Badge variant="outline" className="text-[9px] border-white/10 text-white/60">
                                {tpl.category}
                              </Badge>
                              {tpl.is_system && (
                                <Badge variant="outline" className="text-[9px] border-indigo-500/40 text-indigo-300">
                                  system
                                </Badge>
                              )}
                              {tpl.recommended_special_assessment && (
                                <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-300">
                                  $
                                </Badge>
                              )}
                            </div>
                            <div className="text-white/50 line-clamp-2">{tpl.body}</div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); previewTemplate(tpl); }}
                            title="Preview PDF"
                            className="shrink-0 self-start mt-2 mr-1 rounded-md p-1.5 border border-white/10 text-emerald-300 hover:text-emerald-200 hover:border-emerald-500/40 transition"
                            data-testid={`motion-template-preview-${tpl.code}`}
                          >
                            <Eye className="h-3 w-3" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <select value={linkedSaId} onChange={(e) => setLinkedSaId(e.target.value)}
                       className="h-9 rounded-md bg-white/[0.04] border border-white/10 text-xs px-2 max-w-[220px] text-white"
                       data-testid="motion-link-sa">
                <option value="">Link special assessment…</option>
                {draftSAs.map((s) => (
                  <option key={s.id} value={s.id}>{s.title} · ${(s.total_amount_usd || 0).toLocaleString()}</option>
                ))}
              </select>
              <Button data-testid="propose-motion" variant="outline" className="border-white/10" onClick={propose}>Propose</Button>
            </div>
            {linkedSaId && (
              <div className="text-[10px] text-emerald-300 mb-2 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> On approval + meeting finalize, the sa-auto-bill cron (08:00 UTC daily) will auto-approve and bill this assessment.
              </div>
            )}
          </>
        )}
        <div className="space-y-2">
          {m.motions.map((mo) => {
            const t = tally(mo);
            return (
              <div key={mo.id} className="rounded border border-white/10 bg-white/[0.02] p-2" data-testid={`motion-${mo.id}`}>
                <div className="text-sm">{mo.text}</div>
                {mo.special_assessment_id && (
                  <div className="text-[10px] mt-1 text-emerald-300 flex items-center gap-1"
                       data-testid={`motion-sa-link-${mo.id}`}>
                    <ShieldCheck className="h-3 w-3" /> Linked assessment: <strong>{mo.linked_sa_title || mo.special_assessment_id}</strong>
                    {mo.linked_sa_amount_usd && <span className="text-white/50">· ${mo.linked_sa_amount_usd.toLocaleString()}</span>}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2 text-[11px]">
                  <Badge variant="outline" className={mo.result === "passed" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : mo.result === "failed" ? "border-rose-500/40 text-rose-300 bg-rose-500/10" : "border-white/10"}>{mo.result}</Badge>
                  <span className="text-emerald-300">Yes {t.yes}</span><span className="text-rose-300">No {t.no}</span><span className="text-zinc-400">Abstain {t.abstain}</span>
                  {!locked && mo.result === "open" && <>
                    <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 h-6" onClick={() => vote(mo.id, "yes")} data-testid={`vote-yes-${mo.id}`}><Vote className="h-3 w-3 mr-1" /> Yes</Button>
                    <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-300 h-6" onClick={() => vote(mo.id, "no")} data-testid={`vote-no-${mo.id}`}>No</Button>
                    <Button size="sm" variant="outline" className="border-white/10 h-6" onClick={() => vote(mo.id, "abstain")}>Abstain</Button>
                  </>}
                </div>
              </div>
            );
          })}
          {m.motions.length === 0 && <div className="text-xs text-muted-foreground">No motions yet.</div>}
        </div>
      </div>

      <div>
        <div className="overline text-xs mb-2 flex items-center gap-2"><FileCheck className="h-3 w-3" /> Minutes</div>
        <Textarea data-testid="minutes-text" rows={8} value={minutes} onChange={(e) => setMinutes(e.target.value)} disabled={locked} className="bg-white/[0.04] border-white/10 text-xs font-mono" placeholder="Meeting called to order at 7:00 PM…" />
        {!locked && <Button data-testid="save-minutes" variant="outline" className="border-white/10 mt-2" onClick={saveMinutes}>Save draft</Button>}
      </div>
    </Card>
  );
}

export default function BoardMeetingsPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [openMid, setOpenMid] = useState(null);
  const load = useCallback(async () => {
    // Always send property_id when the context has one selected — otherwise
    // a multi-property admin sees meetings across every community they own
    // (the R108 scope-leak the testing agent flagged).
    if (!propertyId) { setItems([]); return; }
    try {
      const { data } = await api.get(`/board/meetings?property_id=${propertyId}`);
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { scheduled: 0, in_progress: 0, completed: 0 };
    items.forEach((m) => { c[m.status] = (c[m.status] || 0) + 1; });
    return c;
  }, [items]);

  if (openMid) return (
    <div>
      <PageHeader eyebrow="Governance" title="Meeting" subtitle="Attendance · Motions · Minutes · Finalize"
                  actions={<PresenceStrip propertyId={propertyId} testId="board-meeting-presence" />} />
      <MeetingDetail meetingId={openMid} onBack={() => setOpenMid(null)} onChange={load} />
    </div>
  );

  return (
    <div>
      <PageHeader eyebrow="Governance" title="Board Meetings" subtitle="Agenda-to-minutes workflow with roll-call votes, quorum tracking, and hash-locked minutes."
                  actions={<PresenceStrip propertyId={propertyId} testId="board-meetings-presence" />} />
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Scheduled</div><div className="text-2xl font-semibold text-sky-300" data-testid="count-scheduled">{counts.scheduled || 0}</div></Card>
        <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">In progress</div><div className="text-2xl font-semibold text-amber-300">{counts.in_progress || 0}</div></Card>
        <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Completed</div><div className="text-2xl font-semibold text-emerald-300">{counts.completed || 0}</div></Card>
        <Card className="border-white/10 bg-card p-3 flex items-center justify-end"><CreateMeetingDialog propertyId={propertyId} onCreated={load} /></Card>
      </div>
      <div className="space-y-2">
        {items.map((m) => <MeetingRow key={m.id} m={m} onOpen={setOpenMid} />)}
        {items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">No meetings yet. Schedule your first one.</div>}
      </div>
    </div>
  );
}
